/**
 * Llamadas a la API del bot. Una función por endpoint, sin lógica de vista:
 * lo único que hacen es nombrar la URL y tipar lo que vuelve.
 */
import { api } from '@/api/client'
import type { ApiResult, Paginated } from '@/types/api'
import type {
  AutomatedMessage,
  BranchDetail,
  BranchesIndex,
  Campaign,
  ChainSettings,
  ConfigurationChange,
  ConversationDetail,
  ConversationSummary,
  SessionData,
  Stats,
} from '@/types/panel'

// ── Sesión ────────────────────────────────────────────────────────────────

/**
 * La sonda con la que arranca todo. Lleva timeout porque el primer render
 * espera por ella: sin límite, un backend que no contesta deja la pantalla en
 * blanco indefinidamente en vez de acabar en el login.
 */
const SESSION_PROBE_TIMEOUT = 8000

export function fetchSession() {
  return api.get<SessionData>('/api/session', { timeout: SESSION_PROBE_TIMEOUT })
}

// Bajo /api, no en la raíz: el panel tiene su propia página en /login y si el
// endpoint viviera ahí mismo, nginx tendría que rutear por método —GET a la
// SPA, POST a Rails— y una recarga de la página devolvería un 404 de Rails.
export function login(email: string, password: string) {
  return api.post<{ user: SessionData['user']; csrf_token: string }>('/api/login', {
    user: { email, password },
  })
}

export function logout() {
  return api.delete<{ csrf_token: string }>('/api/logout')
}

// ── Estadísticas ──────────────────────────────────────────────────────────

export function fetchStats(params: { days?: number; branch_id?: number | null } = {}) {
  return api.get<Stats>('/api/stats', {
    params: {
      days: params.days ?? 7,
      ...(params.branch_id ? { branch_id: params.branch_id } : {}),
    },
  })
}

// ── Chats ─────────────────────────────────────────────────────────────────

export interface ConversationFilters {
  search?: string
  branch_id?: number | null
  state?: string | null
  /** "open" = derivadas sin cerrar · "none" = las que sigue llevando el bot. */
  handoff?: 'open' | 'none' | null
  days?: number | null
  page?: number
  per_page?: number
}

export function listConversations(filters: ConversationFilters = {}) {
  const params: Record<string, string | number> = {}
  if (filters.search) params.search = filters.search
  if (filters.branch_id) params.branch_id = filters.branch_id
  if (filters.state) params.state = filters.state
  if (filters.handoff) params.handoff = filters.handoff
  if (filters.days) params.days = filters.days
  params.page = filters.page ?? 1
  params.per_page = filters.per_page ?? 25

  return api.get<Paginated<ConversationSummary>>('/api/conversations', { params })
}

export function getConversation(id: number) {
  return api.get<ConversationDetail>(`/api/conversations/${id}`)
}

// ── Anuncios ──────────────────────────────────────────────────────────────

export function listCampaigns(params: { search?: string; page?: number; per_page?: number } = {}) {
  return api.get<Paginated<Campaign>>('/api/campaigns', {
    params: { per_page: 100, ...params },
  })
}

export type CampaignPayload = Partial<
  Pick<
    Campaign,
    | 'key'
    | 'name'
    | 'internal_note'
    | 'active'
    | 'triggers'
    | 'landing_text'
    | 'service_hint'
    | 'promo'
  >
>

export function createCampaign(campaign: CampaignPayload): Promise<ApiResult<Campaign>> {
  return api.post<Campaign>('/api/campaigns', { campaign })
}

export function updateCampaign(id: number, campaign: CampaignPayload): Promise<ApiResult<Campaign>> {
  return api.patch<Campaign>(`/api/campaigns/${id}`, { campaign })
}

export function deleteCampaign(id: number) {
  return api.delete<{ id: number }>(`/api/campaigns/${id}`)
}

export function listAutomatedMessages() {
  return api.get<Paginated<AutomatedMessage>>('/api/automated_messages', {
    params: { per_page: 100 },
  })
}

export type AutomatedMessagePayload = Partial<
  Pick<AutomatedMessage, 'key' | 'match' | 'kind' | 'branch_id' | 'active'>
>

export function createAutomatedMessage(automated_message: AutomatedMessagePayload) {
  return api.post<AutomatedMessage>('/api/automated_messages', { automated_message })
}

export function updateAutomatedMessage(id: number, automated_message: AutomatedMessagePayload) {
  return api.patch<AutomatedMessage>(`/api/automated_messages/${id}`, { automated_message })
}

export function deleteAutomatedMessage(id: number) {
  return api.delete<{ id: number }>(`/api/automated_messages/${id}`)
}

// ── Sucursales ────────────────────────────────────────────────────────────

export function listBranches() {
  return api.get<BranchesIndex>('/api/branches')
}

export function getBranch(id: number) {
  return api.get<BranchDetail>(`/api/branches/${id}`)
}

/**
 * Solo lo que un formulario puede cambiar sin runbook. El ruteo de entrada y
 * `digitail_clinic_id` no están aquí a propósito: la API tampoco los acepta.
 */
export interface BranchPayload {
  name?: string
  timezone?: string
  active?: boolean
  /** El interruptor: `true` hace que la sucursal conteste sola. */
  live?: boolean
  /** Se manda para reemplazarla; la API nunca la devuelve. */
  respira_api_key?: string
  /** Vacío devuelve la sucursal al horario de la cadena. */
  agent_hours?: { from: number; to: number } | ''
}

export function updateBranch(id: number, branch: BranchPayload) {
  return api.patch<BranchDetail>(`/api/branches/${id}`, { branch })
}

export function listConfigurationChanges(params: { record_type?: string; per_page?: number } = {}) {
  return api.get<Paginated<ConfigurationChange>>('/api/configuration_changes', {
    params: { per_page: 25, ...params },
  })
}

export function updateChainSettings(settings: {
  agent_hours_enabled?: boolean
  agent_hours?: { from: number; to: number }
  unified_number_branch_id?: number | ''
}) {
  return api.patch<ChainSettings>('/api/branches_settings', settings)
}
